import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { styled } from "@mui/material/styles";
import autobind from "autobind-decorator";
import { RouterStore } from "mobx-react-router";
import AccountService, { SignupResult } from "../../service/AccountService";
import FileService, { CreateFileResult } from "../../service/FileService";
import { inject, observer } from "mobx-react";
import { CircularProgress } from "@mui/material";
import { observable } from "mobx";
import { sampleFileData } from "../../assets/sampleFlowData";
import { StyledAvatar } from "../common/StyledAvatar";

const Paper = styled("div")((props) => ({
  marginTop: props.theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const Form = styled("form")((props) => ({
  width: "100%", // Fix IE 11 issue.
  marginTop: props.theme.spacing(1),
}));

const SubmitButton = styled(Button)((props) => ({
  margin: props.theme.spacing(3, 0, 2),
  minWidth: "120px",
  height: "38px",
}));

type SignupPageProps = {
  routing?: RouterStore;
  account?: AccountService;
  files?: FileService;
};

type SignupState = "NORMAL" | "LOADING" | SignupResult;

@inject("routing")
@inject("account")
@inject("files")
@observer
export default class SignupPage extends React.Component<SignupPageProps> {
  @observable
  private _status = "NORMAL";

  @observable
  private _mailChimpUrl = `https://flowcharts.us8.list-manage.com/subscribe/post?u=${process.env.REACT_APP_MAILCHIMP_U}&id=${process.env.REACT_APP_MAILCHIMP_ID}`;

  @observable
  private _email = "";

  @observable
  private _password = "";

  @observable
  private _confirmPassword = "";

  getEmailErrorMessage(): string {
    if (this._status == "INVALID_EMAIL") {
      return "Email is invalid.";
    }

    if (this._status == "EMAIL_IN_USE") {
      return "Email is already in use.";
    }

    return null;
  }

  getPasswordErrorMessage(): string {
    if (this._status == "INVALID_PASSWORD") {
      return "Password is too weak. It must have 8 characters minimum.";
    }
    return null;
  }

  getConfirmErrorMessage(): string {
    if (this._status == "CONFIRM_ERROR") {
      return "Password mismatch.";
    }
    return null;
  }

  @autobind
  handleEmailChange(event) {
    this._email = event.target.value?.trim();
  }

  @autobind
  handlePasswordChange(event) {
    this._password = event.target.value;
  }

  @autobind
  handleConfirmPasswordChange(event) {
    this._confirmPassword = event.target.value;
  }

  @autobind
  async handleSubmit(event) {
    event.preventDefault();
    this._status = "LOADING";
    var { account, routing, files } = this.props;
    this._status = await account.handleSignUp(
      this._email,
      this._password,
      this._confirmPassword
    );
    if (this._status == "SUCCESS") {
      this._status = await files.createFile("Example", true);
      if (files.files[0]) {
        await files.saveFileData(files.files[0].fileId, sampleFileData);
      }
    }

    if (this._status == "SUCCESS") {
      routing.replace("/");
    }
  }

  @autobind
  async handleLogin() {
    this.props.routing.push("/login");
  }

  render() {
    var emailError = this.getEmailErrorMessage();
    var passwordError = this.getPasswordErrorMessage();
    var confirmError = this.getConfirmErrorMessage();

    return (
      <Container component="main" maxWidth="xs">
        <Paper>
          <StyledAvatar img={require("../../assets/favicon.ico")} />
          <Typography component="h1" variant="h5">
            Create Account
          </Typography>
          <Form noValidate onSubmit={this.handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              label="Email Address"
              autoFocus
              value={this._email}
              onChange={this.handleEmailChange}
              disabled={this._status == "LOADING"}
              error={emailError != null}
              helperText={emailError}
            />
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              label="Password"
              type="password"
              value={this._password}
              onChange={this.handlePasswordChange}
              disabled={this._status == "LOADING"}
              error={passwordError != null}
              helperText={passwordError}
            />
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              label="Confirm Password"
              type="password"
              value={this._confirmPassword}
              onChange={this.handleConfirmPasswordChange}
              disabled={this._status == "LOADING"}
              error={confirmError != null}
              helperText={confirmError}
            />

            <Grid container direction="row" justifyContent="center">
              <SubmitButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={this._status == "LOADING"}
              >
                {this._status == "LOADING" ? (
                  <CircularProgress size={18} />
                ) : (
                  <Typography>Create Account</Typography>
                )}
              </SubmitButton>

              <div style={{ width: "24px" }} />

              <SubmitButton
                type="button"
                variant="contained"
                color="inherit"
                onClick={this.handleLogin}
                disabled={this._status == "LOADING"}
              >
                <Typography>Log In</Typography>
              </SubmitButton>
            </Grid>
          </Form>
        </Paper>
      </Container>
    );
  }
}
